import { jsx as _jsx } from "react/jsx-runtime";
import { Moon, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';
import { IconButton } from '../../primitives/IconButton/IconButton.js';
const STORAGE_KEY = 'artosera-theme';
const LABELS = {
    fr: { toLight: 'Passer au thème clair', toDark: 'Passer au thème sombre' },
    en: { toLight: 'Switch to light theme', toDark: 'Switch to dark theme' },
};
function readStoredTheme() {
    try {
        const value = window.localStorage.getItem(STORAGE_KEY);
        return value === 'light' || value === 'dark' ? value : null;
    }
    catch {
        return null;
    }
}
/** Le thème posé sur `<html>` par un autre mécanisme (défaut du mode foire). */
function readDocumentTheme() {
    const value = document.documentElement.dataset['theme'];
    return value === 'light' || value === 'dark' ? value : null;
}
function systemPrefersDark() {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}
/**
 * Le thème clair / sombre et son mécanisme de mémorisation, hors de tout
 * bouton : `ThemeToggle` et le menu du compte de la régie s'appuient dessus.
 * Le choix explicite est mémorisé en localStorage et posé en `data-theme` sur
 * `<html>` ; sans choix, le thème suit la préférence système (tokens.css).
 */
export function useThemeChoice() {
    const [theme, setThemeInterne] = useState(() => readStoredTheme() ?? readDocumentTheme() ?? (systemPrefersDark() ? 'dark' : 'light'));
    useEffect(() => {
        document.documentElement.dataset.theme = theme;
    }, [theme]);
    // Le thème peut aussi changer hors de ce bouton (le mode foire pose le sombre
    // par défaut, puis le retire en le quittant) : le bouton suit `<html>` pour
    // ne jamais afficher la lune quand la page est claire.
    useEffect(() => {
        const observer = new MutationObserver(() => {
            setThemeInterne(readDocumentTheme() ?? (systemPrefersDark() ? 'dark' : 'light'));
        });
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme'],
        });
        return () => observer.disconnect();
    }, []);
    const setTheme = (next) => {
        setThemeInterne(next);
        try {
            window.localStorage.setItem(STORAGE_KEY, next);
        }
        catch {
            /* stockage indisponible (navigation privée) : le choix ne survit pas au rechargement */
        }
    };
    return { theme, setTheme };
}
/** Bascule clair / sombre de la régie (3.2). */
export function ThemeToggle({ lang = 'fr' }) {
    const { theme, setTheme } = useThemeChoice();
    const toggle = () => setTheme(theme === 'dark' ? 'light' : 'dark');
    const label = theme === 'dark' ? LABELS[lang].toLight : LABELS[lang].toDark;
    return (_jsx(IconButton, { icon: theme === 'dark' ? _jsx(Moon, { "aria-hidden": "true" }) : _jsx(Sun, { "aria-hidden": "true" }), "aria-label": label, "aria-pressed": theme === 'dark', onClick: toggle }));
}
//# sourceMappingURL=ThemeToggle.js.map